// BHP-1062: the detail page reads from two places on purpose.
//
// `useVps` returns our own row via the Hydra item hook — identity, hostname,
// labels, owner. `useVpsServerState` fetches the live Hetzner state per request
// (BHP-1101); it is plain JSON, not a Hydra resource, so it goes through the
// http client directly instead of `useItem`.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { httpClient } from '../../../shared/http/httpClient';
import { useItem } from '../../../shared/api/useHydra';
import type { VpsSummary } from '../domain/vpsSummary';
import type {
  MetricPeriod,
  MetricType,
  VpsMetrics,
  VpsServerState
} from '../domain/vpsServerState';

/**
 * The state and metrics operations are declared `formats: ['json']` on the
 * backend — they return a computed payload, not an addressable resource, so
 * they never got the JSON-LD treatment. The client defaults to `ld+json` for
 * Hydra, which those endpoints answer with a 406.
 */
const PLAIN_JSON = 'application/json';

export const vpsDetailKeys = {
  state: (uuid: string) => ['vps', uuid, 'server'] as const,
  metrics: (uuid: string, type: MetricType, period: MetricPeriod) =>
    ['vps', uuid, 'metrics', type, period] as const
};

/** Our own row. Empty uuid disables the query so the page can render a 404. */
export function useVps(uuid: string): UseQueryResult<VpsSummary> {
  return useItem<VpsSummary>('vps', uuid, uuid !== '');
}

/**
 * Live state. Refetched on window focus (React Query's default) because a
 * status changes while you are looking at it — a stopped server that someone
 * started elsewhere should not keep reading "off" until a manual reload.
 */
export function useVpsServerState(
  uuid: string
): UseQueryResult<VpsServerState> {
  return useQuery({
    queryKey: vpsDetailKeys.state(uuid),
    queryFn: () =>
      httpClient.get<VpsServerState>(`/vps/${uuid}/server`, {
        accept: PLAIN_JSON
      }),
    enabled: uuid !== ''
  });
}

export function useVpsMetrics(
  uuid: string,
  type: MetricType,
  period: MetricPeriod
): UseQueryResult<VpsMetrics> {
  return useQuery({
    queryKey: vpsDetailKeys.metrics(uuid, type, period),
    queryFn: () =>
      httpClient.get<VpsMetrics>(`/vps/${uuid}/metrics`, {
        accept: PLAIN_JSON,
        params: { resourceType: type, period }
      }),
    enabled: uuid !== ''
  });
}
